import React from 'react';
import {
  CERTIFICATE_STYLES,
  StudentCertificateDesignData,
  formatCertificatePercentage,
  getQrCodeUrl,
  getStudentNameClass,
  resolveCertificateStats,
} from '../../utils/studentCertificateDesign';

interface StudentCertificateProps {
  certificate: StudentCertificateDesignData;
  className?: string;
}

const compact = (value?: string) => String(value || '').replace(/\s+/g, ' ').trim();

const StudentCertificate: React.FC<StudentCertificateProps> = ({ certificate, className = '' }) => {
  const stats = resolveCertificateStats(certificate);
  const examName = compact(certificate.examName || certificate.courseName || certificate.scholarshipName);
  const percentage = certificate.percentage !== undefined
    ? formatCertificatePercentage(certificate.percentage)
    : '';
  const classNameText = compact(certificate.className);
  const subjectNameText = compact(certificate.subjectName);
  const qrCodeUrl = certificate.qrCodeDataUrl || getQrCodeUrl(certificate.verificationUrl);
  const studentNameClass = getStudentNameClass(certificate.studentName);

  return (
    <>
      <style>{CERTIFICATE_STYLES}</style>
      <main
        className={`la-certificate ${className}`}
        aria-label={`${certificate.certificateTitle} for ${certificate.studentName}`}
      >
        <div className="la-frame-outer">
          <div className="la-frame-gold">
            <div className="la-frame-inner">
              <div className="la-wave-lines" />
              <div className="la-corner la-corner-tl" />
              <div className="la-corner la-corner-tr" />
              <div className="la-corner la-corner-bl" />
              <div className="la-corner la-corner-br" />

              <div className="la-ribbon" aria-hidden="true">
                <div className="la-ribbon-label">LA</div>
                <div className="la-ribbon-stars">* * *</div>
              </div>

              <section className="la-content">
                <div className="la-top-row">
                  <div />
                  <div className="la-brand">
                    <div className="la-brand-mark">
                      <span>LA</span>
                    </div>
                    <div>
                      <h1 className="la-brand-title">LocalAlly</h1>
                      <div className="la-brand-tagline">Learn. Compete. Grow.</div>
                    </div>
                  </div>
                  <div className="la-certificate-number">
                    Certificate No.
                    <strong>{certificate.certificateNumber}</strong>
                  </div>
                </div>

                <div className="la-title">{certificate.certificateTitle}</div>
                <div className="la-title-divider" />
                <div className="la-certify">THIS IS TO CERTIFY THAT</div>
                <div className={`la-student-name ${studentNameClass}`}>{certificate.studentName}</div>

                {percentage && examName ? (
                  <div className="la-achievement-text">
                    has achieved <span className="la-highlight">{percentage}</span> in the{' '}
                    <strong>{examName}</strong>
                  </div>
                ) : examName ? (
                  <div className="la-achievement-text">
                    has successfully accomplished <strong>{examName}</strong>
                  </div>
                ) : null}

                {(classNameText || subjectNameText) && (
                  <div className="la-class-subject">
                    {classNameText}
                    {classNameText && subjectNameText && <span>|</span>}
                    {subjectNameText}
                  </div>
                )}

                <div className="la-message">{certificate.achievementMessage}</div>

                {stats.length > 0 && (
                  <div className="la-stats-row">
                    {stats.map((stat) => (
                      <div key={`${stat.label}-${stat.value}`} className="la-stat-card">
                        <div className="la-stat-label">{stat.label}</div>
                        <div className="la-stat-value">{stat.value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {certificate.achievementBadge && (
                <div className="la-achievement-badge">
                  <div className="la-badge-inner">
                    <div className="la-badge-stars">* * *</div>
                    <div className="la-badge-text">{certificate.achievementBadge}</div>
                  </div>
                </div>
              )}

              <div className="la-growth-wreath" aria-hidden="true">
                <div className="la-growth-copy">Keep Learning Keep Growing</div>
              </div>

              <div className="la-bottom-row">
                <div className="la-meta">
                  <span className="la-meta-label">Issue Date</span>
                  {certificate.issueDate}
                </div>
                <div className="la-meta">
                  <span className="la-meta-label">Issued By</span>
                  {certificate.issuedByName}
                </div>
                <div className="la-seal">
                  {certificate.sealUrl ? (
                    <img src={certificate.sealUrl} alt="LocalAlly seal" />
                  ) : (
                    <div className="la-seal-fallback">
                      LocalAlly
                      <span>* * *</span>
                    </div>
                  )}
                </div>
                <div className="la-meta la-signature">
                  {certificate.signatureUrl ? (
                    <img src={certificate.signatureUrl} alt="Authorized signature" />
                  ) : (
                    <div className="la-signature-name">
                      {certificate.authorizedSignatoryName || 'R. K. Verma'}
                    </div>
                  )}
                  <div className="la-signature-line" />
                  <div className="la-signature-title">Authorized Signatory</div>
                  <div className="la-signature-designation">
                    {certificate.authorizedSignatoryDesignation || 'Authorized Signatory'}
                  </div>
                </div>
                <div className="la-verification">
                  <img src={qrCodeUrl} alt="Certificate verification QR code" />
                  <div className="la-verification-text">Scan to Verify</div>
                </div>
              </div>

              <div className="la-verification-footer">
                Verify this certificate at: {certificate.verificationUrl}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default StudentCertificate;
